import request from "../../service/request";
import { logout } from "./logout";

import { getLocalStorage, removeLocalStorage, setLocalStorage } from "../utils/localStorage";
import { getSessionStorage } from "../utils/sessionStorage";
import {
  isExpiryFeatureEnabled,
  isGuestUser,
  updateExpiryFromResultObj,
  getExpiresAt,
  isRenewalInCooldown,
  getRemainingCooldownMs,
  markRenewalFailed,
  markRenewalSucceeded,
} from "../utils/tokenExpiryHelpers";

import constants from "../constants";
import storageConstants from "../constants/storage";
import error from "../constants/error";
import { ANALYTICS_STORAGE_KEYS } from "../constants/analytics";
import { DT_EVENTS, DT_ATTRIBUTES } from "../constants/dynatrace";

import { logDTEvent, isDynatraceTrackingEnabled } from "../analytics/dynatrace";

const {
  APP_ENVIRONMENTS,
  API_BASE_URL,
  MAX_ACCOUNT_PROFILES,
  CASSANDRA_KEEPALIVE_INTERVAL,
  USER_SESSION_RENEWAL_INTERVAL,
} = constants;

const { AVS_REFRESH_TOKEN, USER_PROFILE, SESSION_EXPIRED } = storageConstants;

const { AVS_ERROR_CODES } = error;

const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;

// Check if app is interactive for an authenticated user
function appIsInteractive() {
  // consider interactive if we have a session id set after profile load
  return !!getSessionStorage(ANALYTICS_STORAGE_KEYS.SESSION_ID);
}

/* eslint-disable no-unused-vars */

const checkProfileResponse = (res, appProvider, avsRefreshToken) => {
  if (isExpiryFeatureEnabled() && !res) {
    return {
      keepAlive: USER_SESSION_RENEWAL_INTERVAL,
      profile: createUserProfile(false, null, null),
    };
  }
  // Pull store user profile json
  let userProfile = getLocalStorage(USER_PROFILE);

  // If cassandra is down based on the error codes from
  // devicesessions, we should user the cache user profile
  if (
    res.data?.resultCode === "KO" &&
    (res.data?.errorDescription === AVS_ERROR_CODES.INVALID_STREAM_SESSION ||
      res.data?.errorDescription === AVS_ERROR_CODES.INTERNAL_SERVER_ERROR ||
      res.data?.errorDescription === AVS_ERROR_CODES.CASSANDRA_OUTAGE ||
      res.data?.errorDescription === AVS_ERROR_CODES.MS_OUTAGE ||
      res.data?.errorDescription === AVS_ERROR_CODES.SERVICE_OUTAGE_CODES[0])
  ) {
    if (userProfile?.length) {
      userProfile = JSON.parse(userProfile);
    }

    return {
      keepAlive: CASSANDRA_KEEPALIVE_INTERVAL,
      profile: createUserProfile(userProfile?.length ? true : false, userProfile, null),
    };
  } else if (
    res.data?.errorDescription === AVS_ERROR_CODES.TOKEN_NOT_VALID ||
    res.data?.errorDescription === AVS_ERROR_CODES.INVALID_REFRESH_TOKEN ||
    res.data?.errorDescription === AVS_ERROR_CODES.USER_NOT_LOGIN
  ) {
    removeLocalStorage(AVS_REFRESH_TOKEN);
    return {
      keepAlive: CASSANDRA_KEEPALIVE_INTERVAL,
      profile: createUserProfile(false, userProfile?.data?.resultObj, null),
      headers: res.headers,
    };
  } else {
    return returnAVSUserProfile(appProvider, avsRefreshToken)
      .then((profile) => {
        // Storing userprofile to local storage for future usage
        setLocalStorage(USER_PROFILE, JSON.stringify(profile?.data?.resultObj));

        return {
          keepAlive: profile.data.resultObj.keepAlive,
          profile: createUserProfile(true, profile?.data?.resultObj, null),
          headers: profile.headers,
        };
      })
      .catch(async (error) => {
        const errorCode = error?.data?.errorDescription || error?.response?.data?.errorDescription;
        if (isExpiryFeatureEnabled() && errorCode === AVS_ERROR_CODES.TOKEN_NOT_VALID) {
          // Reactive handling ONLY for 403-10100 error during bootstrap
          try {
            await doAccessTokenRenewal(appProvider);
            // Retry PROFILE once after successful renewal
            const retryProfile = await returnAVSUserProfile(appProvider, avsRefreshToken);
            setLocalStorage(USER_PROFILE, JSON.stringify(retryProfile?.data?.resultObj));
            return {
              keepAlive: retryProfile.data.resultObj.keepAlive,
              profile: createUserProfile(true, retryProfile?.data?.resultObj, null),
              headers: retryProfile.headers,
            };
          } catch {
            // Renewal failed or non-recoverable → fall through
          }
        }
        return {
          keepAlive: res.data.resultObj.keepAlive,
          profile: createUserProfile(false, null, null),
        };
      });
  }
};

/* Process User Profile **/
export function getUserProfile(appProvider, authUserContent) {
  let avsRefreshToken = getLocalStorage(AVS_REFRESH_TOKEN);
  if (avsRefreshToken !== null) {
    return renewUserSession(appProvider, avsRefreshToken)
      .then((res) => {
        return checkProfileResponse(res, appProvider, avsRefreshToken);
      })
      .catch(async (res) => {
        if (isExpiryFeatureEnabled()) {
          // Bootstrap recovery: run shared renewal
          try {
            const renewalRes = await doAccessTokenRenewal(appProvider);
            return checkProfileResponse(renewalRes, appProvider, avsRefreshToken);
          } catch (renewErr) {
            // Renewal decided to stop (e.g., expired + server error or invalid refresh)
            return checkProfileResponse(renewErr, appProvider, avsRefreshToken);
          }
        }
        return checkProfileResponse(res, appProvider, avsRefreshToken);
      });
  } else {
    avsRefreshToken = authUserContent?.headers[AVS_REFRESH_TOKEN];
    return returnAVSUserProfile(appProvider, avsRefreshToken)
      .then((profile) =>
        renewUserSession(appProvider, avsRefreshToken)
          .then((res) => {
            return {
              keepAlive: res.data.resultObj.keepAlive,
              profile: createUserProfile(true, profile?.data?.resultObj, null),
              headers: profile?.headers,
            };
          })
          .catch(async (res) => {
            if (isExpiryFeatureEnabled()) {
              // Bootstrap recovery: run shared renewal
              try {
                const renewalRes = await doAccessTokenRenewal(appProvider);
                return {
                  keepAlive: renewalRes.data.resultObj.keepAlive,
                  profile: createUserProfile(true, profile?.data?.resultObj, null),
                  headers: profile?.headers,
                };
              } catch (renewErr) {
                // Renewal decided to stop (e.g., expired + server error or invalid refresh)
                return checkProfileResponse(renewErr, appProvider, avsRefreshToken);
              }
            }
            return checkProfileResponse(res, appProvider, avsRefreshToken);
          })
      )
      .catch(async (error) => {
        const errorCode = error?.data?.errorDescription || error?.response?.data?.errorDescription;
        if (isExpiryFeatureEnabled() && isGuestUser() === false && errorCode === AVS_ERROR_CODES.TOKEN_NOT_VALID) {
          // Reactive handling ONLY for 403-10100 error during bootstrap
          try {
            await doAccessTokenRenewal(appProvider);
            // Retry PROFILE once after successful renewal
            const retryProfile = await returnAVSUserProfile(appProvider, avsRefreshToken);
            return renewUserSession(appProvider, avsRefreshToken)
              .then((res) => {
                return {
                  keepAlive: res.data.resultObj.keepAlive,
                  profile: createUserProfile(true, retryProfile?.data?.resultObj, null),
                  headers: retryProfile?.headers,
                };
              })
              .catch(() => {
                // If renewUserSession fails after successful PROFILE, fall back safely
                return {
                  keepAlive: USER_SESSION_RENEWAL_INTERVAL,
                  profile: createUserProfile(true, retryProfile?.data?.resultObj, null),
                  headers: retryProfile?.headers,
                };
              });
          } catch {
            // Renewal failed or non-recoverable → proceed to login failure handling
            handleLoginFailure(error);
            return;
          }
        }
        handleLoginFailure(error);
      });
  }
}

// /* Extend User Session **/
export function extendUserSession(appProvider, avsRefreshToken = null, renewBeforeExpiry = false) {
  return renewUserSession(appProvider, avsRefreshToken, renewBeforeExpiry)
    .then((res) => {
      if (isExpiryFeatureEnabled() && renewBeforeExpiry) {
        // set the new access token expiry timestamp from the renewal response
        const ok = updateExpiryFromResultObj(res?.data?.resultObj);
        if (!ok) {
          const err = new Error("expire_at missing on renewal");
          err.code = "EXPIRE_AT_MISSING";
          throw err;
        }
      }
      return res;
    })
    .catch((err) => err);
}

// HELPERS //

/**
 * Creates a User profile object
 * @param Boolean login status
 * @param User user object
 * @param String Access token for the user
 */
function createUserProfile(isLoggedIn, user, accessToken, errorInfo = null) {
  return { isLoggedIn, user, accessToken, errorInfo, masterAccountHouseholdID: null };
}

function handleLoginFailure(error) {
  if (error && error.type && error.type === "browser") {
    return {
      keepAlive: USER_SESSION_RENEWAL_INTERVAL,
      profile: createUserProfile(false, null, null),
    };
  }
}

/**
 * Promise Handler - Return AVS user profile
 */
function returnAVSUserProfile(appProvider, avsRefreshToken = null) {
  let url = appProvider.config.avs_api_urls.user_profile;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url: url,
    method: "GET",
    withCredentials: true,
    returnResponseObject: true,
  };

  if (avsRefreshToken !== null) {
    options.headers = {};
    options.headers[AVS_REFRESH_TOKEN] = avsRefreshToken;
  }

  return request(options, null, appProvider);
}

/**
 * Update profile details
 * @param {Object} appProvider
 * @param {Object} profileParams
 * @returns {Promise}
 */
export function updateProfile(appProvider, profileParams) {
  let url = appProvider.config.avs_api_urls.user_profile;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
    data: profileParams,
  };

  return request(options, null, appProvider);
}

/**
 * Create a new profile (can only be used to create sub-account profiles)
 * @param {Object} appProvider
 * @param {Object} profileParams
 * @param {Object} cancelTokenSource CancelToken - deprecated in v0.22.0, will have to update to AbortController if we update Axios in the future
 * @returns {Promise}
 */
export function createProfile(appProvider, profileParams, cancelTokenSource = null) {
  let url = appProvider.config.avs_api_urls.subaccount_profile;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
    data: profileParams,
  };

  return request(options, cancelTokenSource, appProvider);
}

/**
 * Get list of profiles
 * @param {Object} appProvider
 * @param {Object} cancelTokenSource CancelToken - deprecated in v0.22.0, will have to update to AbortController if we update Axios in the future
 * @returns {Promise}
 */
export function getUserProfileList(appProvider, cancelTokenSource = null) {
  let url = appProvider.config.avs_api_urls.list_profiles;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "GET",
    withCredentials: true,
  };

  return request(options, cancelTokenSource, appProvider).then((result) => {
    return result?.familyAccountList?.filter((profile) => profile.status === "Active")?.slice(0, MAX_ACCOUNT_PROFILES);
  });
}

/**
 * Get list of available avatars
 * @param {Object} appProvider
 * @param {Object} cancelTokenSource CancelToken - deprecated in v0.22.0, will have to update to AbortController if we update Axios in the future
 * @returns {Promise}
 */
export function getAvatarList(appProvider, cancelTokenSource = null) {
  let url = appProvider.config.avs_api_urls.list_avatars;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "GET",
    withCredentials: true,
  };

  return request(options, cancelTokenSource, appProvider);
}

/**
 * Delete a profile
 * @param {Object} appProvider
 * @param {Number} userId
 * @returns {Promise}
 */
export function deleteProfile(appProvider, userId) {
  let url = appProvider.config.avs_api_urls.user_profile;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);
  url = `${url}/${userId}`;

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "DELETE",
    withCredentials: true,
  };

  return request(options, null, appProvider);
}

/**
 * Log in to subaccount profile
 * @param {Object} appProvider
 * @param {Number} userId
 * @param {String} entryPin
 * @returns {Promise}
 */
export function loginProfile(appProvider, userId, entryPin = null) {
  let url = appProvider.config.avs_api_urls.subaccount_login;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const data = userId ? { userId } : {};
  if (entryPin) {
    data.entryPin = entryPin;
  }

  const options = {
    url,
    method: "POST",
    withCredentials: true,
    returnResponseObject: true,
    data,
  };

  const avsRefreshToken = getLocalStorage(AVS_REFRESH_TOKEN);
  if (avsRefreshToken !== null) {
    options.headers = {
      [AVS_REFRESH_TOKEN]: avsRefreshToken,
    };
  }

  return request(options, null, appProvider).then((response) => {
    return response.data.resultObj;
  });
}

/**
 * Log out from subaccount profile
 * @param {Object} appProvider
 * @returns {Promise}
 */
export function logoutProfile(appProvider) {
  let url = appProvider.config.avs_api_urls.subaccount_logout;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url,
    method: "DELETE",
    withCredentials: true,
    returnResponseObject: true,
  };

  const avsRefreshToken = getLocalStorage(AVS_REFRESH_TOKEN);
  if (avsRefreshToken !== null) {
    options.headers = {
      [AVS_REFRESH_TOKEN]: avsRefreshToken,
    };
  }

  return request(options, null, appProvider).then((response) => {
    return response.data.resultObj;
  });
}

/**
 * Promise Handler - Returns user session cookies are valid or not
 */
function renewUserSession(appProvider, avsRefreshToken = null, renewBeforeExpiry = false) {
  let url = appProvider.config.avs_api_urls.user_session_renewal;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const options = {
    url: url,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      renewTokensBeforeExpiration: renewBeforeExpiry,
    },
    withCredentials: true,
    returnResponseObject: true,
  };

  if (avsRefreshToken !== null) {
    options.headers[AVS_REFRESH_TOKEN] = avsRefreshToken;
  }

  return request(options, null, appProvider);
}

/**
 * Video stream heartbeat request that also fetches content entitlements. The pcPin parameter can be
 * used to unlock Parental Control blocked content.
 * @param {Object} appProvider
 * @param {String} contentType
 * @param {String|Number} contentId
 * @param {String|Number} assetId
 * @param {String} pcPin Optional Parental Control PIN
 * @param {Object} cancelTokenSource CancelToken - deprecated in v0.22.0, will have to update to AbortController if we update Axios in the fugure
 * @param {String} avsRefreshToken
 * @returns {Promise}
 */
export function videoStreamHeartBeat(
  appProvider,
  contentType,
  contentId,
  assetId,
  pcPin = null,
  cancelTokenSource = null,
  avsRefreshToken = null
) {
  let url = appProvider.config.avs_api_urls.video_heart_beat;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);
  url = url.replace("$CONTENT_TYPE", contentType);
  url = url.replace("$CONTENT_ID", contentId);
  url = url.replace("$ASSET_ID", assetId);
  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
  }

  const headers = { "Content-Type": "application/json" };
  if (pcPin) {
    headers.pcpin = pcPin;
  }

  const options = {
    url,
    method: "POST",
    headers,
    data: {},
    withCredentials: true,
    returnResponseObject: true,
  };

  if (avsRefreshToken !== null) {
    options.headers = {
      [AVS_REFRESH_TOKEN]: avsRefreshToken,
    };
  }

  return request(options, cancelTokenSource, appProvider).then((res) => {
    if (isExpiryFeatureEnabled()) {
      updateExpiryFromResultObj(res?.data?.resultObj);
    }
  });
}

/* Helpers for doAccessTokenRenewal */
function isSuccessStatus(status) {
  return status >= 200 && status < 400;
}
function isClientSideErrorStatus(status) {
  return status >= 400 && status < 500;
}
function isServerSideErrorStatus(status) {
  return status >= 500;
}
function getErrorCode(res) {
  return res?.data?.errorDescription || res?.response?.data?.errorDescription;
}

let inflightRequest = null; // to handle concurrency

/**
 * Shared Access Token Renewal Handler
 * @param {Object} appProvider app provider object
 * @param {Boolean} isPreflightMode Optional flag to indicate preflight mode (default: false)
 * @returns {Promise}
 */
export const doAccessTokenRenewal = async (appProvider, isPreflightMode = false) => {
  if (!isExpiryFeatureEnabled()) return; // FF check
  if (isGuestUser()) return; // unauth user check

  const token = getLocalStorage(AVS_REFRESH_TOKEN);
  if (!token) return; // no token check

  // check if in cooldown period
  if (isRenewalInCooldown()) {
    const remainingMs = getRemainingCooldownMs();
    console.warn(`🛑 Token renewal blocked - cooldown active for ${Math.ceil(remainingMs / 1000)}s`);

    return;
  }

  if (inflightRequest) return inflightRequest; // concurrency check

  inflightRequest = (async () => {
    const now = Date.now();
    const expiresAt = getExpiresAt();
    const isStillValid = expiresAt && now < expiresAt;

    const appState = document.visibilityState === "visible" ? "foreground" : "background";

    // Main thread always calls USER/DEVICESESSIONS (no playback context)
    // Service Worker handles playback heartbeat calls separately
    const apiPath = "USER/DEVICESESSIONS";

    // Attempt 1: try access token renewal
    const firstAttemptRes = await extendUserSession(appProvider, token, true);
    const firstAttemptStatus = firstAttemptRes?.status;
    const errorCode = getErrorCode(firstAttemptRes);

    // Success path
    if (isSuccessStatus(firstAttemptStatus)) {
      markRenewalSucceeded(); // clear cooldown on success
      const ok = updateExpiryFromResultObj(firstAttemptRes?.data?.resultObj);
      if (!ok) throw Object.assign(new Error("expire_at missing on renewal"), { code: "EXPIRE_AT_MISSING" });
      return firstAttemptRes;
    }

    // Refresh token invalid/expired path
    if (errorCode === AVS_ERROR_CODES.INVALID_REFRESH_TOKEN) {
      markRenewalFailed(); // mark failure and start cooldown
      // immediate logout
      logDTEvent(
        DT_EVENTS.TOKEN_REFRESH_ERROR,
        {
          [DT_ATTRIBUTES.ERROR_CODE]: errorCode,
          [DT_ATTRIBUTES.API_PATH]: apiPath,
          [DT_ATTRIBUTES.APP_STATE]: appState,
          [DT_ATTRIBUTES.LOGGED_OUT]: true,
        },
        isDynatraceTrackingEnabled()
      );
      if (appIsInteractive()) setLocalStorage(SESSION_EXPIRED, true);
      await logout(appProvider);
      throw firstAttemptRes;
    }

    // Access token invalid path
    if (errorCode === AVS_ERROR_CODES.TOKEN_NOT_VALID) {
      // Attempt 2: retry once for expired access token errors
      const secondAttemptRes = await extendUserSession(appProvider, token, true);
      const secondAttemptStatus = secondAttemptRes?.status;

      // retry success path
      if (isSuccessStatus(secondAttemptStatus)) {
        markRenewalSucceeded(); // clear cooldown on success
        const ok = updateExpiryFromResultObj(secondAttemptRes?.data?.resultObj);
        if (!ok) throw Object.assign(new Error("expire_at missing on renewal"), { code: "EXPIRE_AT_MISSING" });
        return secondAttemptRes;
      }

      // Both attempts failed
      markRenewalFailed(); // mark failure and start cooldown

      logDTEvent(
        DT_EVENTS.TOKEN_REFRESH_ERROR,
        {
          [DT_ATTRIBUTES.ERROR_CODE]: errorCode,
          [DT_ATTRIBUTES.API_PATH]: apiPath,
          [DT_ATTRIBUTES.APP_STATE]: appState,
          [DT_ATTRIBUTES.LOGGED_OUT]: false,
        },
        isDynatraceTrackingEnabled()
      );
      if (isPreflightMode && isStillValid) {
        // if access token was still valid at start of renewal flow
        // Continue with original call for prefight mode
        return;
      } else {
        // don't retry, don't logout, don't continue with original call
        throw secondAttemptRes;
      }
    }

    // Catch-all: do not logout, stop renewal path
    if (
      isClientSideErrorStatus(firstAttemptStatus) ||
      isServerSideErrorStatus(firstAttemptStatus) ||
      errorCode === AVS_ERROR_CODES.TOKEN_SERVICE_ERROR
    ) {
      markRenewalFailed(); // mark failure and start cooldown
      logDTEvent(
        DT_EVENTS.TOKEN_REFRESH_ERROR,
        {
          [DT_ATTRIBUTES.ERROR_CODE]: errorCode,
          [DT_ATTRIBUTES.API_PATH]: apiPath,
          [DT_ATTRIBUTES.APP_STATE]: appState,
          [DT_ATTRIBUTES.LOGGED_OUT]: false,
        },
        isDynatraceTrackingEnabled()
      );
      if (isPreflightMode && isStillValid) {
        // if access token was still valid at start of renewal flow
        // Continue with original call for prefight mode
        return;
      } else {
        // don't continue with original call
        throw firstAttemptRes;
      }
    }
  })();

  try {
    await inflightRequest;
  } catch (err) {
    // catch any unhandled errors and mark failure
    markRenewalFailed();
    throw err;
  } finally {
    inflightRequest = null;
  }
};
